import { RENEWAL_STEP_EVENT } from './constants';

/**
 * Client side of renewal observability (TDR-renewal-observability, Layer 2): one
 * `renewal_step` event per step of the renewal UI, `{ step, outcome, errorCode,
 * provider, appVersion }`. `errorCode` is set only for error states (RN-xxx); named
 * so because the analytics middleware redacts any `code` property.
 * banner_shown carries the surface in `outcome`, cta_clicked the action pressed.
 * The API's `renewal.member_action` log stays the authoritative copy; this one adds
 * what the server cannot see (shown, clicked, started but never finished).
 */

export const RENEWAL_STEPS = Object.freeze({
  STATUS_LOAD: 'status_load',
  BANNER_SHOWN: 'banner_shown',
  CTA_CLICKED: 'cta_clicked',
  CARD_UPDATE_STARTED: 'card_update_started',
  CARD_UPDATE_SUCCEEDED: 'card_update_succeeded',
  CARD_UPDATE_FAILED: 'card_update_failed',
  RETRY_RESULT: 'retry_result',
  WINBACK_QUOTE: 'winback_quote',
  WINBACK_RENEW: 'winback_renew',
  CANCEL: 'cancel',
  RESUME: 'resume',
  SAVE_OFFER: 'save_offer',
});

// The canonical `outcome` vocabulary for both clients (mobile mirrors it): the API's
// `renewal.member_action` outcomes, plus the two the client alone sees. Per step:
//   status_load            failed (sent only when the read fails)
//   banner_shown           RENEWAL_SURFACES value (where it was shown), not an outcome
//   cta_clicked            RENEWAL_ACTIONS value (what was pressed), not an outcome
//   card_update_started    ok | redirected (sent to Shopify's update page)
//   card_update_succeeded  ok
//   card_update_failed     declined (bank said no) | refused (form incomplete; on
//                          mobile, the member cancelled) |
//                          failed (API, network) — see getStripeErrorOutcome
//   retry_result           getRetryOutcome(retry.status); sent only when a retry ran
//   winback_quote          ok | unavailable | refused | requires_action
//   winback_renew          ok | declined | requires_action | refused | redirected |
//                          failed
//   cancel                 ok (confirmed) | pending (still landing) | failed
//   resume                 ok | failed
//   save_offer             ok (offer accepted) | unavailable (offer unreadable) |
//                          failed (accept failed)
// `failed` is the alerting outcome: our fault, never the member's.
export const RENEWAL_STEP_OUTCOMES = Object.freeze({
  OK: 'ok',
  DECLINED: 'declined',
  REQUIRES_ACTION: 'requires_action',
  REFUSED: 'refused',
  NOT_FOUND: 'not_found',
  UNAVAILABLE: 'unavailable',
  FAILED: 'failed',
  REDIRECTED: 'redirected',
  PENDING: 'pending',
  SKIPPED: 'skipped',
});

// Where a banner or sheet was shown: banner_shown's `outcome`.
export const RENEWAL_SURFACES = Object.freeze({
  ACCOUNT_BANNER: 'account_banner',
  ENTRY_SHEET: 'entry_sheet',
  PAUSED_SHEET: 'paused_sheet',
});

// Every renewal error state the member can see (or that fails silently), shown as
// "Error RN-xxx" and sent as the event's `errorCode`, so a screenshot from support maps
// straight to a PostHog query.
export const RENEWAL_ERROR_CODES = Object.freeze({
  STATUS_UNAVAILABLE: 'RN-001',
  SETUP_INTENT_FAILED: 'RN-101',
  CARD_CONFIRM_FAILED: 'RN-102',
  SETUP_INTENT_UNREADABLE: 'RN-103',
  CARD_NOT_ACCEPTED: 'RN-104',
  SUBSCRIPTION_UNAVAILABLE: 'RN-105',
  CARD_NOT_SAVED: 'RN-106',
  RETRY_DECLINED: 'RN-201',
  RETRY_FAILED: 'RN-202',
  PAYMENT_LINK_FAILED: 'RN-301',
  PAYMENT_LINK_NOT_SENT: 'RN-302',
  CHECKOUT_UNAVAILABLE: 'RN-401',
  WINBACK_QUOTE_UNAVAILABLE: 'RN-402',
  WINBACK_RENEW_FAILED: 'RN-403',
  WINBACK_OUTCOME_UNKNOWN: 'RN-404',
  WINBACK_NO_PAYMENT_PAGE: 'RN-405',
  WINBACK_DECLINED: 'RN-406',
  RESUME_FAILED: 'RN-501',
  CANCEL_FAILED: 'RN-601',
  SAVE_OFFER_UNAVAILABLE: 'RN-602',
  SAVE_OFFER_ACCEPT_FAILED: 'RN-603',
});

// The immediate retry's status (PAYMENT_UPDATE_RETRY_STATUS) as an outcome.
const RETRY_STATUS_OUTCOMES = Object.freeze({
  paid: RENEWAL_STEP_OUTCOMES.OK,
  declined: RENEWAL_STEP_OUTCOMES.DECLINED,
  requires_action: RENEWAL_STEP_OUTCOMES.REQUIRES_ACTION,
  pending: RENEWAL_STEP_OUTCOMES.PENDING,
  failed: RENEWAL_STEP_OUTCOMES.FAILED,
  skipped: RENEWAL_STEP_OUTCOMES.SKIPPED,
});

/**
 * @param {string|undefined} retryStatus - `retry.status` from PATCH /subscriptions
 * @return {string|null} The outcome, the raw status when it is not a known one, or
 *   null with no retry
 */
export const getRetryOutcome = (retryStatus) => {
  if (!retryStatus) return null;
  return RETRY_STATUS_OUTCOMES[retryStatus] || retryStatus;
};

// Stripe.js error types that are the member's to fix, not ours.
const STRIPE_ERROR_TYPES = Object.freeze({
  CARD_ERROR: 'card_error',
  VALIDATION_ERROR: 'validation_error',
});

/**
 * @param {object|null} stripeError - a Stripe.js error (confirmSetup)
 * @return {string} DECLINED for the bank's no, REFUSED for an incomplete form,
 *   FAILED for anything else (API, network, rate limit)
 */
export const getStripeErrorOutcome = (stripeError) => {
  switch (stripeError?.type) {
    case STRIPE_ERROR_TYPES.CARD_ERROR:
      return RENEWAL_STEP_OUTCOMES.DECLINED;
    case STRIPE_ERROR_TYPES.VALIDATION_ERROR:
      return RENEWAL_STEP_OUTCOMES.REFUSED;
    default:
      return RENEWAL_STEP_OUTCOMES.FAILED;
  }
};

/**
 * A Stripe.js error's code joined to ours: the decline reason when Stripe gives one
 * (insufficient_funds) over the generic one (card_declined).
 *
 * @param {string} code - RENEWAL_ERROR_CODES value
 * @param {object|null} stripeError
 * @return {string} e.g. "RN-102:insufficient_funds"
 */
export const withStripeErrorCode = (code, stripeError) => {
  const stripeCode = stripeError?.decline_code || stripeError?.code || stripeError?.type;
  return stripeCode ? `${code}:${stripeCode}` : code;
};

/**
 * @param {string} code
 * @return {string} What the member sees, e.g. "Error RN-101"
 */
export const formatRenewalErrorCode = (code) => `Error ${code}`;

// AlertProvider's own copy for an alert with no message.
const ALERT_FALLBACK_MESSAGE = 'Something went wrong. Please contact support@fordays.com';

/**
 * @param {string|null|undefined} message
 * @param {string} code
 * @return {string} The message (or the alert's generic one) followed by the code
 */
export const withRenewalErrorCode = (message, code) => (
  `${message || ALERT_FALLBACK_MESSAGE} (${formatRenewalErrorCode(code)})`
);

/**
 * @param {object} params
 * @param {string} params.step - RENEWAL_STEPS value
 * @param {string} [params.outcome] - RENEWAL_STEP_OUTCOMES value; for banner_shown a
 *   RENEWAL_SURFACES value, for cta_clicked a RENEWAL_ACTIONS value
 * @param {string|null} [params.errorCode] - RENEWAL_ERROR_CODES value (with the Stripe
 *   code for a confirmSetup failure); error states only
 * @param {string|null} [params.provider] - RENEWAL_PROVIDERS value, sent as-is (STRIPE /
 *   SHOPIFY) to match the API's `renewal.member_action` and mobile
 * @return {object} The event's properties
 */
export const buildRenewalStepProperties = ({
  step, outcome = RENEWAL_STEP_OUTCOMES.OK, errorCode = null, provider = null,
}) => ({
  step,
  outcome,
  errorCode: errorCode || null,
  provider: provider || null,
  appVersion: process.env.REACT_APP_VERSION || null,
});

/**
 * Sends one `renewal_step` event. Never throws: analytics must not break a renewal.
 *
 * @param {object|null|undefined} posthog - from usePostHog()
 * @param {object} params - see buildRenewalStepProperties
 */
export const trackRenewalStep = (posthog, params) => {
  try {
    posthog?.capture(RENEWAL_STEP_EVENT, buildRenewalStepProperties(params));
  } catch (e) {
    // Dropped: the server's renewal.member_action still has the authoritative copy.
  }
};
